/**
 * Organization Setup Wizard
 * 8-step hierarchical wizard for super admins to onboard organizations:
 * 1. Organization basics
 * 2. Programs (with record model selection)
 * 3. Record Model Config (household/episode settings)
 * 4. Affiliations (program → health plan/MCP linkage)
 * 5. Configuration (field mappings, import rules, billing, reporting per affiliation)
 * 6. Staff assignments
 * 7. Form Templates (clone from another org)
 * 8. Review & Activate
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  organizationSetupAPI,
  type ProgramAffiliation,
  type ProgramAffiliationCreate,
  type AffiliationFieldMapping,
  type AffiliationImportRules,
  type AffiliationReportConfig,
  type AffiliationBillingConfig,
  type StaffProgramAssignment,
  type StaffAssignmentCreate,
  type SemanticFieldGroup,
  type NewCaseRule,
  type RecordModel,
  type OrganizationSetupDraft,
} from '@/services/api/organizationSetup';
import { organizationsAPI, type Organization } from '@/services/api/organizations';
import { formsAPI } from '@/services/api/forms';
import { healthPlansAPI } from '@/services/api/healthPlans';
import { claimsApi, type Payer } from '@/services/api/claims';
import {
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Building2,
  Target,
  FileStack,
  Network,
  Settings,
  Users,
  AlertCircle,
  Plus,
  Trash2,
  Search,
  Wand2,
  Upload,
  GripVertical,
  Shield,
  FileText,
  DollarSign,
  BarChart3,
  Loader2,
  X,
  Copy,
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface OrganizationSetupWizardProps {
  cboId?: number;
  draftId?: number;
  onComplete?: (cboId: number) => void;
  onCancel?: () => void;
}

interface OrgFormData {
  id?: number;
  name: string;
  description: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  website: string;
}

interface ProgramFormData {
  id?: number;
  name: string;
  program_type: string;
  description: string;
  start_date: string;
  end_date: string;
  capacity: number | null;
  record_model: RecordModel;
}

interface AffiliationFormData extends ProgramAffiliationCreate {
  payer_name?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const WIZARD_STEPS = [
  {
    id: 1,
    name: 'Organization',
    icon: Building2,
    description: 'Basic info',
    guidance: 'Select an existing organization or create a new one. Enter the basic details.',
  },
  {
    id: 2,
    name: 'Programs',
    icon: Target,
    description: 'Add programs',
    guidance: 'Add one or more programs to this organization. Select the program type and record model for each.',
  },
  {
    id: 3,
    name: 'Record Model',
    icon: FileStack,
    description: 'Configure records',
    guidance: 'Configure how records are managed for each program. Household programs track families; Episode programs track sequential cases per member.',
  },
  {
    id: 4,
    name: 'Affiliations',
    icon: Network,
    description: 'Link health plans',
    guidance: 'Link each program to its health plans / MCPs. This determines which plans send members and how data flows.',
  },
  {
    id: 5,
    name: 'Configuration',
    icon: Settings,
    description: 'Per-MCP settings',
    guidance: 'Configure each affiliation: field mappings, import rules, billing, and reporting. Settings are scoped to each program-MCP combination.',
  },
  {
    id: 6,
    name: 'Staff',
    icon: Users,
    description: 'Assign staff',
    guidance: 'Assign staff members to programs and optionally to specific MCPs. Set roles and caseload limits.',
  },
  {
    id: 7,
    name: 'Form Templates',
    icon: FileText,
    description: 'Clone forms',
    guidance: 'Optionally clone form templates from an existing organization to give this new org a head start with intake forms, assessments, and care plans.',
  },
  {
    id: 8,
    name: 'Review',
    icon: CheckCircle2,
    description: 'Activate',
    guidance: 'Review all configuration and activate the organization setup.',
  },
];

const PROGRAM_TYPES = [
  { value: 'CalAIM_ECM', label: 'CalAIM Enhanced Care Management', suggestedModel: 'EPISODE' as RecordModel },
  { value: 'CalAIM_CS', label: 'CalAIM Community Supports', suggestedModel: 'EPISODE' as RecordModel },
  { value: 'DCM', label: 'Disaster Case Management', suggestedModel: 'HOUSEHOLD' as RecordModel },
  { value: 'Doula', label: 'Doula Services', suggestedModel: 'EPISODE' as RecordModel },
  { value: 'CHW', label: 'Community Health Worker', suggestedModel: 'EPISODE' as RecordModel },
  { value: 'Housing', label: 'Housing Services', suggestedModel: 'STANDARD' as RecordModel },
  { value: 'Food', label: 'Food & Nutrition', suggestedModel: 'STANDARD' as RecordModel },
  { value: 'Transportation', label: 'Transportation', suggestedModel: 'STANDARD' as RecordModel },
  { value: 'Behavioral_Health', label: 'Behavioral Health', suggestedModel: 'STANDARD' as RecordModel },
  { value: 'Preventive_Care', label: 'Preventive Care', suggestedModel: 'STANDARD' as RecordModel },
  { value: 'Organizing', label: 'Organizing', suggestedModel: 'STANDARD' as RecordModel },
  { value: 'Other', label: 'Other', suggestedModel: 'STANDARD' as RecordModel },
];

const RECORD_MODELS: { value: RecordModel; label: string; description: string }[] = [
  { value: 'STANDARD', label: 'Standard', description: 'One record per member per program' },
  { value: 'HOUSEHOLD', label: 'Household', description: 'Track household units with individual members (DCM)' },
  { value: 'EPISODE', label: 'Episode', description: 'Multiple sequential cases per member over time (ECM, Doula, CHW)' },
];

const AFFILIATION_TYPES = [
  { value: 'MCP', label: 'Managed Care Plan (MCP)' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'MEDICARE', label: 'Medicare' },
  { value: 'SPONSOR', label: 'Sponsor' },
  { value: 'PARTNER', label: 'Partner' },
];

const DETECTION_TYPES = [
  { value: 'STATUS_CHANGE', label: 'Field value match' },
  { value: 'DATE_GAP', label: 'Date gap threshold' },
  { value: 'FIELD_CHANGE', label: 'Field value changed' },
  { value: 'MANUAL', label: 'Manual only' },
];

const DETECTION_ACTIONS = [
  { value: 'AUTO_CREATE', label: 'Auto-create new case' },
  { value: 'PROMPT_USER', label: 'Prompt user to decide' },
  { value: 'FLAG_FOR_REVIEW', label: 'Flag for review' },
];

const HOUSEHOLD_SERVICE_CATEGORIES = [
  'Housing assistance', 'Insurance claims', 'Debris removal', 'Financial aid',
  'Temporary shelter', 'Mental health support', 'Employment', 'Education',
  'Transportation', 'Healthcare', 'Childcare', 'Legal services',
];

const PROGRAM_ROLES = [
  { value: 'lead', label: 'Program Lead' },
  { value: 'coordinator', label: 'Care Coordinator' },
  { value: 'specialist', label: 'Specialist' },
  { value: 'viewer', label: 'Viewer (Read-only)' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export const OrganizationSetupWizard: React.FC<OrganizationSetupWizardProps> = ({
  cboId,
  draftId,
  onComplete,
  onCancel,
}) => {
  const tabbed = useTabbedChrome();
  // ── State ──────────────────────────────────────────────────────────────
  const { fmt } = useFormattedDate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draft, setDraft] = useState<OrganizationSetupDraft | null>(null);

  // Step 1: Organization
  const [orgMode, setOrgMode] = useState<'existing' | 'new'>(cboId ? 'existing' : 'new');
  const [orgData, setOrgData] = useState<OrgFormData>({
    name: '', description: '', contact_email: '', contact_phone: '', address: '', website: '',
  });
  const [existingOrgs, setExistingOrgs] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>(cboId);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');

  // Step 2: Programs
  const [programs, setPrograms] = useState<ProgramFormData[]>([]);

  // Step 3: Record Model Config (per-program)
  const [householdConfigs, setHouseholdConfigs] = useState<Record<number, {
    serviceCategories: string[];
    customCategories: string[];
    numberPrefix: string;
  }>>({});
  const [episodeConfigs, setEpisodeConfigs] = useState<Record<number, {
    rules: NewCaseRule[];
    numberPrefix: string;
    allowReopenClosed: boolean;
    showArchivedTimeline: boolean;
  }>>({});

  // Step 4: Affiliations (per-program)
  const [affiliations, setAffiliations] = useState<Record<number, ProgramAffiliation[]>>({});
  const [affiliationForms, setAffiliationForms] = useState<Record<number, AffiliationFormData>>({});

  // Step 5: Configuration (per-affiliation)
  const [fieldMappings, setFieldMappings] = useState<Record<number, AffiliationFieldMapping[]>>({});
  const [importRules, setImportRules] = useState<Record<number, Partial<AffiliationImportRules>>>({});
  const [reportConfigs, setReportConfigs] = useState<Record<number, AffiliationReportConfig[]>>({});
  const [billingConfigs, setBillingConfigs] = useState<Record<number, Partial<AffiliationBillingConfig>>>({});
  const [activeConfigAffiliation, setActiveConfigAffiliation] = useState<number | null>(null);
  const [activeConfigTab, setActiveConfigTab] = useState('field-mapping');

  // Step 6: Staff
  const [orgStaff, setOrgStaff] = useState<any[]>([]);
  const [staffAssignments, setStaffAssignments] = useState<StaffProgramAssignment[]>([]);
  const [staffForm, setStaffForm] = useState<Partial<StaffAssignmentCreate>>({});

  // Step 7: Form Templates (clone from another org)
  const [cloneSourceCboId, setCloneSourceCboId] = useState<string>('');
  const [availableFormTemplates, setAvailableFormTemplates] = useState<any[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<number>>(new Set());
  const [cloneSourceOrgName, setCloneSourceOrgName] = useState<string>('');
  const [formTemplatesLoading, setFormTemplatesLoading] = useState(false);
  const [clonedTemplates, setClonedTemplates] = useState<any[]>([]);

  // Custom category input (per-program, keyed by programIndex)
  const [newCustomCategory, setNewCustomCategory] = useState<Record<number, string>>({});

  // Step 5: Semantic groups (loaded once)
  const [semanticGroups, setSemanticGroups] = useState<SemanticFieldGroup[]>([]);

  // Available payers + health plans for affiliation step
  const [availablePayers, setAvailablePayers] = useState<Payer[]>([]);
  const [availableHealthPlans, setAvailableHealthPlans] = useState<any[]>([]);
  const [affiliationSearchResults, setAffiliationSearchResults] = useState<any[]>([]);
  const [healthPlanSearch, setHealthPlanSearch] = useState('');
  const [affiliationSearchMode, setAffiliationSearchMode] = useState<'payer' | 'health_plan'>('payer');
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Auto-save infrastructure
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [showDraftResume, setShowDraftResume] = useState(false);
  const [existingDrafts, setExistingDrafts] = useState<OrganizationSetupDraft[]>([]);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftInitializedRef = useRef(false);

  // ── Collect all wizard state into a single object ─────────────────────

  const collectWizardState = useCallback(() => ({
    orgMode, orgData, selectedOrgId,
    programs,
    householdConfigs, episodeConfigs,
    affiliations, affiliationForms,
    fieldMappings, importRules, reportConfigs, billingConfigs,
    staffAssignments,
  }), [orgMode, orgData, selectedOrgId, programs, householdConfigs, episodeConfigs,
       affiliations, affiliationForms, fieldMappings, importRules, reportConfigs,
       billingConfigs, staffAssignments]);

  // ── Draft Persistence (Server-Side) ─────────────────────────────────

  const saveDraft = useCallback(async (forceDraftId?: number) => {
    const id = forceDraftId || draft?.id;
    if (!id) return;
    setSavingDraft(true);
    try {
      await organizationSetupAPI.updateDraft(id, {
        wizard_state: collectWizardState(),
        current_step: currentStep,
        status: 'IN_PROGRESS',
      });
      setLastSavedAt(fmt.time(new Date()));
    } catch (err) {
      console.error('Draft auto-save failed:', err);
    } finally {
      setSavingDraft(false);
    }
  }, [draft?.id, collectWizardState, currentStep]);

  const scheduleSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      saveDraft();
    }, 2000); // Debounce: save 2 seconds after last change
  }, [saveDraft]);

  const loadDraft = async (id: number) => {
    try {
      setLoading(true);
      const d = await organizationSetupAPI.getDraft(id);
      setDraft(d);
      if (d.wizard_state) {
        const s = d.wizard_state;
        if (s.orgMode) setOrgMode(s.orgMode);
        if (s.orgData) setOrgData(s.orgData);
        if (s.selectedOrgId) setSelectedOrgId(s.selectedOrgId);
        if (s.programs) setPrograms(s.programs);
        if (s.householdConfigs) setHouseholdConfigs(s.householdConfigs);
        if (s.episodeConfigs) setEpisodeConfigs(s.episodeConfigs);
        if (s.affiliations) setAffiliations(s.affiliations);
        if (s.affiliationForms) setAffiliationForms(s.affiliationForms);
        if (s.fieldMappings) setFieldMappings(s.fieldMappings);
        if (s.importRules) setImportRules(s.importRules);
        if (s.reportConfigs) setReportConfigs(s.reportConfigs);
        if (s.billingConfigs) setBillingConfigs(s.billingConfigs);
        if (s.staffAssignments) setStaffAssignments(s.staffAssignments);
      }
      setCurrentStep(d.current_step || 1);
      draftInitializedRef.current = true;
    } catch (err) {
      console.error('Failed to load draft:', err);
    } finally {
      setLoading(false);
    }
  };

  const createOrResumeDraft = async () => {
    // If draftId prop provided, load it
    if (draftId) {
      await loadDraft(draftId);
      return;
    }
    // Check for existing in-progress drafts for this user
    try {
      const drafts = await organizationSetupAPI.listDrafts();
      const activeDrafts = drafts.filter((d: any) =>
        d.status === 'DRAFT' || d.status === 'IN_PROGRESS'
      );
      if (activeDrafts.length > 0) {
        setExistingDrafts(activeDrafts);
        setShowDraftResume(true);
        return;
      }
    } catch (err) {
      console.error('Failed to check existing drafts:', err);
    }
    // No existing drafts — create a new one
    await createNewDraft();
  };

  const createNewDraft = async () => {
    try {
      const cboForDraft = selectedOrgId || cboId || 0;
      const newDraft = await organizationSetupAPI.createDraft({
        cbo_id: cboForDraft,
        wizard_state: collectWizardState(),
        current_step: 1,
      });
      setDraft(newDraft);
      draftInitializedRef.current = true;
      setShowDraftResume(false);
    } catch (err) {
      console.error('Failed to create draft:', err);
      // Still allow wizard to function without persistence
      draftInitializedRef.current = true;
    }
  };

  const resumeExistingDraft = async (draftToResume: OrganizationSetupDraft) => {
    setShowDraftResume(false);
    await loadDraft(draftToResume.id);
  };

  const discardAndCreateNew = async () => {
    // Abandon all existing drafts
    for (const d of existingDrafts) {
      try {
        await organizationSetupAPI.deleteDraft(d.id);
      } catch (err) {
        // ignore — draft may already be gone
      }
    }
    setExistingDrafts([]);
    setShowDraftResume(false);
    await createNewDraft();
  };

  // ── Effects ────────────────────────────────────────────────────────────

  // Initialize draft on mount (create or resume)
  useEffect(() => {
    loadExistingOrgs();
    createOrResumeDraft();
    return () => {
      // Cleanup debounce timer
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  // Load payers + health plans when reaching step 4 (affiliations)
  useEffect(() => {
    if (currentStep === 4 && availablePayers.length === 0 && availableHealthPlans.length === 0) {
      loadPayersAndPlans();
    }
  }, [currentStep]);

  // Load semantic groups when reaching step 5 (deferred — requires migration 209)
  useEffect(() => {
    if (currentStep === 5 && semanticGroups.length === 0) {
      loadSemanticGroups();
    }
  }, [currentStep]);

  // Auto-save immediately on step change
  useEffect(() => {
    if (draftInitializedRef.current && draft?.id) {
      saveDraft();
    }
  }, [currentStep]);

  // Auto-save (debounced) whenever wizard data changes
  useEffect(() => {
    if (draftInitializedRef.current && draft?.id) {
      scheduleSave();
    }
  }, [orgMode, orgData, selectedOrgId, programs, householdConfigs, episodeConfigs,
      affiliations, fieldMappings, importRules, reportConfigs, billingConfigs,
      staffAssignments]);

  const loadExistingOrgs = async () => {
    try {
      const orgs = await organizationsAPI.listOrganizations();
      setExistingOrgs(orgs);
      // If we have a cboId, populate orgData from the matching org
      if (cboId) {
        const current = orgs.find(o => o.id === cboId);
        if (current) {
          setOrgData({
            id: current.id,
            name: current.name,
            description: current.description || '',
            contact_email: current.contact_email || '',
            contact_phone: current.contact_phone || '',
            address: current.address || '',
            website: current.website || '',
          });
        }
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const loadSemanticGroups = async () => {
    try {
      const groups = await organizationSetupAPI.listSemanticGroups();
      setSemanticGroups(groups);
    } catch (err) {
      console.error('Failed to load semantic groups:', err);
    }
  };

  const loadPayersAndPlans = async () => {
    try {
      const [payers, hpResponse] = await Promise.all([
        claimsApi.listPayers().catch(() => [] as Payer[]),
        healthPlansAPI.listHealthPlans({ limit: 50, status: 'ACTIVE' }).catch(() => ({ plans: [] })),
      ]);
      setAvailablePayers(payers);
      setAvailableHealthPlans(hpResponse.plans || []);
    } catch (err) {
      console.error('Failed to load payers/plans:', err);
    }
  };

  const searchAffiliationEntities = (search: string) => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setAffiliationSearchResults([]);
      setShowManualEntry(false);
      return;
    }
    // Search payers
    const matchedPayers = availablePayers
      .filter(p => p.payer_name.toLowerCase().includes(q) || (p.edi_payer_id || '').toLowerCase().includes(q))
      .map(p => ({ ...p, _source: 'payer' as const, _display: p.payer_name, _sub: p.payer_type }));
    // Search health plans
    const matchedPlans = availableHealthPlans
      .filter((hp: any) => (hp.name || '').toLowerCase().includes(q) || (hp.short_name || '').toLowerCase().includes(q) || (hp.electronic_payer_id || '').toLowerCase().includes(q))
      .map((hp: any) => ({ ...hp, _source: 'health_plan' as const, _display: hp.name || hp.short_name, _sub: hp.plan_type }));
    const combined = [...matchedPayers, ...matchedPlans];
    setAffiliationSearchResults(combined);
    setShowManualEntry(combined.length === 0);
  };

  const loadOrgStaff = async (orgId: number) => {
    try {
      const staff = await organizationSetupAPI.listOrgStaff(orgId);
      setOrgStaff(staff.map((s: any) => ({ ...s, id: s.id ?? s.user_id })));
    } catch (err) {
      console.error('Failed to load org staff:', err);
    }
  };

  // ── Navigation ─────────────────────────────────────────────────────────

  const handleNext = () => {
    setError(null);

    // Validate current step
    if (currentStep === 1) {
      if (orgMode === 'existing' && !selectedOrgId) {
        setError('Please select an organization.');
        return;
      }
      if (orgMode === 'new' && !orgData.name.trim()) {
        setError('Please enter a name for the new organization.');
        return;
      }
    }
    if (currentStep === 2) {
      if (programs.length === 0) {
        setError('Please add at least one program.');
        return;
      }
      for (const p of programs) {
        if (!p.name.trim() || !p.program_type) {
          setError('All programs must have a name and type.');
          return;
        }
      }
    }
    if (currentStep === 3) {
      // Record model config — optional but shown for non-STANDARD programs
    }
    if (currentStep === 4) {
      // Affiliations are optional — can be added later
    }

    // Skip step 3 if no HOUSEHOLD or EPISODE programs
    if (currentStep === 2) {
      const hasNonStandard = programs.some(p => p.record_model !== 'STANDARD');
      if (!hasNonStandard) {
        setCurrentStep(4);
        return;
      }
    }

    // Skip step 3 when going forward from step 2 if already going to 3
    setCurrentStep(prev => Math.min(prev + 1, WIZARD_STEPS.length));
  };

  const handleBack = () => {
    setError(null);
    if (currentStep === 4) {
      const hasNonStandard = programs.some(p => p.record_model !== 'STANDARD');
      if (!hasNonStandard) {
        setCurrentStep(2);
        return;
      }
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleActivate = async () => {
    if (!selectedOrgId) {
      setError('No organization selected.');
      return;
    }
    setLoading(true);
    try {
      await organizationSetupAPI.activateOrganization(selectedOrgId);
      if (draft?.id) {
        await organizationSetupAPI.updateDraft(draft.id, { status: 'COMPLETED' });
      }
      onComplete?.(selectedOrgId);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Activation failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Program Helpers ────────────────────────────────────────────────────

  const addProgram = () => {
    setPrograms(prev => [...prev, {
      name: '', program_type: '', description: '', start_date: '', end_date: '',
      capacity: null, record_model: 'STANDARD',
    }]);
  };

  const updateProgram = (index: number, field: keyof ProgramFormData, value: any) => {
    setPrograms(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-suggest record model when program type changes
      if (field === 'program_type') {
        const typeInfo = PROGRAM_TYPES.find(t => t.value === value);
        if (typeInfo) {
          updated[index].record_model = typeInfo.suggestedModel;
        }
      }
      return updated;
    });
  };

  const removeProgram = (index: number) => {
    setPrograms(prev => prev.filter((_, i) => i !== index));
  };

  // ── Affiliation Helpers ────────────────────────────────────────────────

  const addAffiliation = (programIndex: number) => {
    const form = affiliationForms[programIndex];
    if (!form?.payer_id && !form?.health_plan_id) return;

    const newAff: ProgramAffiliation = {
      id: Date.now(), // Temp ID until saved
      program_id: programs[programIndex].id || programIndex,
      cbo_id: selectedOrgId || 0,
      affiliation_type: form.affiliation_type || 'MCP',
      payer_id: form.payer_id,
      health_plan_id: form.health_plan_id,
      status: 'ACTIVE',
      payer_name: form.payer_name,
      contract_number: form.contract_number,
      mcp_program_code: form.mcp_program_code,
      mcp_provider_id: form.mcp_provider_id,
    };

    setAffiliations(prev => ({
      ...prev,
      [programIndex]: [...(prev[programIndex] || []), newAff],
    }));
    setAffiliationForms(prev => ({ ...prev, [programIndex]: {} as AffiliationFormData }));
  };

  const removeAffiliation = (programIndex: number, affIndex: number) => {
    setAffiliations(prev => ({
      ...prev,
      [programIndex]: (prev[programIndex] || []).filter((_, i) => i !== affIndex),
    }));
  };

  // ── Episode Rule Helpers ───────────────────────────────────────────────

  const addEpisodeRule = (programIndex: number) => {
    setEpisodeConfigs(prev => {
      const existing = prev[programIndex] || { rules: [], numberPrefix: '', allowReopenClosed: false, showArchivedTimeline: true };
      return {
        ...prev,
        [programIndex]: {
          ...existing,
          rules: [...existing.rules, {
            rule_name: '',
            detection_type: 'STATUS_CHANGE',
            detection_config: {},
            on_detection_action: 'PROMPT_USER',
            auto_archive_previous: true,
            is_active: true,
          }],
        },
      };
    });
  };

  const updateEpisodeRule = (programIndex: number, ruleIndex: number, field: string, value: any) => {
    setEpisodeConfigs(prev => {
      const config = { ...prev[programIndex] };
      const rules = [...config.rules];
      rules[ruleIndex] = { ...rules[ruleIndex], [field]: value };
      return { ...prev, [programIndex]: { ...config, rules } };
    });
  };

  const removeEpisodeRule = (programIndex: number, ruleIndex: number) => {
    setEpisodeConfigs(prev => {
      const config = { ...prev[programIndex] };
      return { ...prev, [programIndex]: { ...config, rules: config.rules.filter((_, i) => i !== ruleIndex) } };
    });
  };

  // ── Staff Helpers ──────────────────────────────────────────────────────

  const addStaffAssignment = () => {
    if (!staffForm.user_id || !staffForm.program_id) return;
    const newAssignment: StaffProgramAssignment = {
      id: Date.now(),
      user_id: staffForm.user_id,
      cbo_id: selectedOrgId || 0,
      program_id: staffForm.program_id,
      affiliation_id: staffForm.affiliation_id,
      role_in_program: staffForm.role_in_program || 'coordinator',
      is_primary: false,
      max_caseload: staffForm.max_caseload,
      effective_date: staffForm.effective_date,
      user_name: orgStaff.find(s => s.id === staffForm.user_id)?.name || `User ${staffForm.user_id}`,
      program_name: programs.find((_, i) => i === staffForm.program_id)?.name,
    };
    setStaffAssignments(prev => [...prev, newAssignment]);
    setStaffForm({});
  };

  const removeStaffAssignment = (index: number) => {
    setStaffAssignments(prev => prev.filter((_, i) => i !== index));
  };

  // ── Step Rendering ─────────────────────────────────────────────────────

  const renderStep1_Organization = () => {
    const filteredOrgs = existingOrgs.filter(org =>
      !orgSearchQuery || org.name.toLowerCase().includes(orgSearchQuery.toLowerCase())
    );
    const selectedOrg = existingOrgs.find(o => o.id === selectedOrgId);

    return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Organization Details</h2>

      {/* Mode selection */}
      <div className="flex gap-3">
        <button
          className={`flex-1 p-4 rounded-lg border-2 text-left transition-colors ${
            orgMode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setOrgMode('existing')}
        >
          <div className="font-medium text-sm">Use Existing Organization</div>
          <div className="text-xs text-gray-500 mt-1">Select from organizations already in the system</div>
        </button>
        <button
          className={`flex-1 p-4 rounded-lg border-2 text-left transition-colors ${
            orgMode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => { setOrgMode('new'); setSelectedOrgId(undefined); }}
        >
          <div className="font-medium text-sm">Create New Organization</div>
          <div className="text-xs text-gray-500 mt-1">Set up a brand new organization from scratch</div>
        </button>
      </div>

      {/* Existing org selector */}
      {orgMode === 'existing' && (
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Search Organizations</Label>
            <div className="flex gap-2">
              <Input
                value={orgSearchQuery}
                onChange={(e) => setOrgSearchQuery(e.target.value)}
                placeholder="Type to filter..."
                className="text-sm"
              />
              <span className="text-xs text-gray-400 self-center whitespace-nowrap">
                {filteredOrgs.length} found
              </span>
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
            {filteredOrgs.map(org => (
              <button
                key={org.id}
                className={`w-full text-left px-4 py-3 transition-colors ${
                  selectedOrgId === org.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50'
                }`}
                onClick={() => {
                  setSelectedOrgId(org.id);
                  setOrgData({
                    id: org.id,
                    name: org.name,
                    description: org.description || '',
                    contact_email: org.contact_email || '',
                    contact_phone: org.contact_phone || '',
                    address: org.address || '',
                    website: org.website || '',
                  });
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{org.name}</div>
                    <div className="text-xs text-gray-500">
                      {org.contact_email || 'No email'} {org.status && <Badge variant="outline" className="ml-2 text-xs">{org.status}</Badge>}
                    </div>
                  </div>
                  {selectedOrgId === org.id && <CheckCircle2 className="h-5 w-5 text-blue-500" />}
                </div>
              </button>
            ))}
            {filteredOrgs.length === 0 && (
              <p className="text-center text-gray-400 text-sm py-4">No organizations found.</p>
            )}
          </div>

          {/* Show selected org details */}
          {selectedOrg && (
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="text-sm">
                <div className="font-medium">{selectedOrg.name}</div>
                {selectedOrg.contact_email && <div className="text-gray-600">Email: {selectedOrg.contact_email}</div>}
                {selectedOrg.address && <div className="text-gray-600">Address: {selectedOrg.address}</div>}
                {selectedOrg.contact_phone && <div className="text-gray-600">Phone: {selectedOrg.contact_phone}</div>}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* New org form */}
      {orgMode === 'new' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Organization Name *</Label>
              <Input
                value={orgData.name}
                onChange={(e) => setOrgData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter organization name"
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={orgData.contact_email}
                onChange={(e) => setOrgData(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="admin@org.com"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={orgData.contact_phone}
                onChange={(e) => setOrgData(prev => ({ ...prev, contact_phone: e.target.value }))}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <Label>Website</Label>
              <Input
                value={orgData.website}
                onChange={(e) => setOrgData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://www.example.org"
              />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={orgData.address}
              onChange={(e) => setOrgData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Street address, City, State ZIP"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={orgData.description}
              onChange={(e) => setOrgData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of the organization"
              rows={3}
            />
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderStep2_Programs = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Programs</h2>
        <Button onClick={addProgram} size="sm">
          <Plus className="h-4 w-4 mr-1" /> Add Program
        </Button>
      </div>

      {programs.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Target className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>No programs added yet. Click "Add Program" to get started.</p>
        </div>
      )}

      {programs.map((program, index) => (
        <Card key={index} className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <Badge variant="outline">Program {index + 1}</Badge>
            <Button variant="ghost" size="sm" onClick={() => removeProgram(index)}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Program Name *</Label>
              <Input
                value={program.name}
                onChange={(e) => updateProgram(index, 'name', e.target.value)}
                placeholder="e.g., CalAIM ECM - LA County"
              />
            </div>
            <div>
              <Label>Program Type *</Label>
              <Select
                value={program.program_type}
                onValueChange={(val) => updateProgram(index, 'program_type', val)}
              >
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {PROGRAM_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={program.start_date}
                onChange={(e) => updateProgram(index, 'start_date', e.target.value)}
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={program.end_date}
                onChange={(e) => updateProgram(index, 'end_date', e.target.value)}
              />
            </div>
            <div>
              <Label>Capacity</Label>
              <Input
                type="number"
                value={program.capacity ?? ''}
                onChange={(e) => updateProgram(index, 'capacity', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Max members"
              />
            </div>
          </div>

          {/* Record Model Selection */}
          <div>
            <Label className="text-sm font-medium">Record Model</Label>
            <div className="mt-2 space-y-2">
              {RECORD_MODELS.map(model => (
                <label
                  key={model.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    program.record_model === model.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={`record_model_${index}`}
                    value={model.value}
                    checked={program.record_model === model.value}
                    onChange={() => updateProgram(index, 'record_model', model.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-sm">{model.label}</div>
                    <div className="text-xs text-gray-500">{model.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={program.description}
              onChange={(e) => updateProgram(index, 'description', e.target.value)}
              placeholder="Brief program description"
              rows={2}
            />
          </div>
        </Card>
      ))}
    </div>
  );

  const renderStep3_RecordModel = () => {
    const nonStandardPrograms = programs
      .map((p, i) => ({ ...p, _index: i }))
      .filter(p => p.record_model !== 'STANDARD');

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Record Model Configuration</h2>

        {nonStandardPrograms.map(program => (
          <Card key={program._index} className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Badge>{program.record_model}</Badge>
              <span className="font-medium">{program.name || `Program ${program._index + 1}`}</span>
            </div>

            {program.record_model === 'HOUSEHOLD' && renderHouseholdConfig(program._index)}
            {program.record_model === 'EPISODE' && renderEpisodeConfig(program._index)}
          </Card>
        ))}

        {nonStandardPrograms.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No programs require record model configuration. All programs use the Standard model.</p>
          </div>
        )}
      </div>
    );
  };

  const renderHouseholdConfig = (programIndex: number) => {
    const config = householdConfigs[programIndex] || {
      serviceCategories: [...HOUSEHOLD_SERVICE_CATEGORIES.slice(0, 6)],
      customCategories: [],
      numberPrefix: '',
    };

    const customCategoryInput = newCustomCategory[programIndex] || '';

    const toggleCategory = (cat: string) => {
      const current = config.serviceCategories;
      const updated = current.includes(cat)
        ? current.filter(c => c !== cat)
        : [...current, cat];
      setHouseholdConfigs(prev => ({
        ...prev,
        [programIndex]: { ...config, serviceCategories: updated },
      }));
    };

    const deleteCategory = (cat: string) => {
      setHouseholdConfigs(prev => ({
        ...prev,
        [programIndex]: {
          ...config,
          serviceCategories: config.serviceCategories.filter(c => c !== cat),
          customCategories: config.customCategories.filter(c => c !== cat),
        },
      }));
    };

    const addCustomCategory = () => {
      const trimmed = customCategoryInput.trim();
      if (!trimmed) return;
      if (config.serviceCategories.includes(trimmed) || HOUSEHOLD_SERVICE_CATEGORIES.includes(trimmed)) return;
      setHouseholdConfigs(prev => ({
        ...prev,
        [programIndex]: {
          ...config,
          serviceCategories: [...config.serviceCategories, trimmed],
          customCategories: [...config.customCategories, trimmed],
        },
      }));
      setNewCustomCategory(prev => ({ ...prev, [programIndex]: '' }));
    };

    // Preset categories not currently selected (available to add back)
    const availablePresets = HOUSEHOLD_SERVICE_CATEGORIES.filter(
      cat => !config.serviceCategories.includes(cat)
    );

    return (
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-medium">Household Service Categories</Label>
          <p className="text-xs text-gray-500 mb-2">
            Manage which service types to track for households. Remove categories not needed for this org.
          </p>

          {/* Selected categories as removable tags */}
          {config.serviceCategories.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-3">
              {config.serviceCategories.map(cat => (
                <Badge
                  key={cat}
                  variant={config.customCategories.includes(cat) ? 'default' : 'secondary'}
                  className="flex items-center gap-1 pl-2.5 pr-1 py-1"
                >
                  {cat}
                  <button
                    type="button"
                    onClick={() => deleteCategory(cat)}
                    className="ml-1 rounded-full p-0.5 hover:bg-black/10 transition-colors"
                    title={`Remove ${cat}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 mb-3">No categories selected. Add from presets or create custom.</p>
          )}

          {/* Add preset categories back */}
          {availablePresets.length > 0 && (
            <div className="mb-3">
              <Label className="text-xs text-gray-500 mb-1 block">Add from presets</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {availablePresets.map(cat => (
                  <label key={cat} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={false}
                      onCheckedChange={() => toggleCategory(cat)}
                    />
                    {cat}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Add custom category */}
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Add custom category</Label>
            <div className="flex gap-2 max-w-md">
              <Input
                value={customCategoryInput}
                onChange={(e) => setNewCustomCategory(prev => ({ ...prev, [programIndex]: e.target.value }))}
                placeholder="e.g., Disaster relief"
                className="text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomCategory(); } }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustomCategory}
                disabled={!customCategoryInput.trim()}
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium">Household Number Prefix</Label>
          <p className="text-xs text-gray-500 mb-1">Default: H{'{org_id}{sequence}'}. Leave blank for auto.</p>
          <Input
            value={config.numberPrefix}
            onChange={(e) => setHouseholdConfigs(prev => ({
              ...prev,
              [programIndex]: { ...config, numberPrefix: e.target.value },
            }))}
            placeholder="Auto-generated"
            className="max-w-xs"
          />
        </div>
      </div>
    );
  };

  const renderEpisodeConfig = (programIndex: number) => {
    const config = episodeConfigs[programIndex] || {
      rules: [],
      numberPrefix: '',
      allowReopenClosed: false,
      showArchivedTimeline: true,
    };

    return (
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-medium">Case Number Prefix</Label>
          <p className="text-xs text-gray-500 mb-1">Default: C{'{org_id}{prog_id}{sequence}'}. Leave blank for auto.</p>
          <Input
            value={config.numberPrefix}
            onChange={(e) => setEpisodeConfigs(prev => ({
              ...prev,
              [programIndex]: { ...config, numberPrefix: e.target.value },
            }))}
            placeholder="Auto-generated"
            className="max-w-xs"
          />
        </div>

        {/* New Case Detection Rules */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="text-sm font-medium">New Case Detection Rules</Label>
              <p className="text-xs text-gray-500">When should the system detect a new case for a member?</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => addEpisodeRule(programIndex)}>
              <Plus className="h-3 w-3 mr-1" /> Add Rule
            </Button>
          </div>

          {config.rules.map((rule, ruleIndex) => (
            <Card key={ruleIndex} className="p-4 space-y-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Rule {ruleIndex + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeEpisodeRule(programIndex, ruleIndex)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Rule Name</Label>
                  <Input
                    value={rule.rule_name}
                    onChange={(e) => updateEpisodeRule(programIndex, ruleIndex, 'rule_name', e.target.value)}
                    placeholder="e.g., ECM Re-assignment"
                  />
                </div>
                <div>
                  <Label className="text-xs">Trigger Type</Label>
                  <Select
                    value={rule.detection_type}
                    onValueChange={(val) => updateEpisodeRule(programIndex, ruleIndex, 'detection_type', val)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DETECTION_TYPES.map(dt => (
                        <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {rule.detection_type === 'STATUS_CHANGE' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Field</Label>
                    <Input
                      value={rule.detection_config?.field || ''}
                      onChange={(e) => updateEpisodeRule(programIndex, ruleIndex, 'detection_config', {
                        ...rule.detection_config, field: e.target.value,
                      })}
                      placeholder="e.g., member_record_status"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Value</Label>
                    <Input
                      value={rule.detection_config?.value || ''}
                      onChange={(e) => updateEpisodeRule(programIndex, ruleIndex, 'detection_config', {
                        ...rule.detection_config, value: e.target.value, condition: 'equals',
                      })}
                      placeholder="e.g., New"
                    />
                  </div>
                </div>
              )}

              {rule.detection_type === 'DATE_GAP' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Date Field</Label>
                    <Input
                      value={rule.detection_config?.field || ''}
                      onChange={(e) => updateEpisodeRule(programIndex, ruleIndex, 'detection_config', {
                        ...rule.detection_config, field: e.target.value,
                      })}
                      placeholder="e.g., due_date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Threshold (days)</Label>
                    <Input
                      type="number"
                      value={rule.detection_config?.threshold_days || ''}
                      onChange={(e) => updateEpisodeRule(programIndex, ruleIndex, 'detection_config', {
                        ...rule.detection_config, threshold_days: parseInt(e.target.value) || 0,
                      })}
                      placeholder="e.g., 300"
                    />
                  </div>
                </div>
              )}

              {rule.detection_type === 'FIELD_CHANGE' && (
                <div>
                  <Label className="text-xs">Field to Monitor</Label>
                  <Input
                    value={rule.detection_config?.field || ''}
                    onChange={(e) => updateEpisodeRule(programIndex, ruleIndex, 'detection_config', {
                      ...rule.detection_config, field: e.target.value, condition: 'changed',
                    })}
                    placeholder="e.g., health_plan_member_id"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">On Detection</Label>
                  <Select
                    value={rule.on_detection_action}
                    onValueChange={(val) => updateEpisodeRule(programIndex, ruleIndex, 'on_detection_action', val)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DETECTION_ACTIONS.map(da => (
                        <SelectItem key={da.value} value={da.value}>{da.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <Checkbox
                    checked={rule.auto_archive_previous}
                    onCheckedChange={(checked) => updateEpisodeRule(programIndex, ruleIndex, 'auto_archive_previous', checked)}
                  />
                  <Label className="text-xs">Archive previous case automatically</Label>
                </div>
              </div>
            </Card>
          ))}

          {config.rules.length === 0 && (
            <p className="text-xs text-gray-400 italic">No rules configured. Cases will only be created manually.</p>
          )}
        </div>

        {/* Episode Options */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={config.allowReopenClosed}
              onCheckedChange={(checked) => setEpisodeConfigs(prev => ({
                ...prev,
                [programIndex]: { ...config, allowReopenClosed: !!checked },
              }))}
            />
            Allow reopening closed cases
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={config.showArchivedTimeline}
              onCheckedChange={(checked) => setEpisodeConfigs(prev => ({
                ...prev,
                [programIndex]: { ...config, showArchivedTimeline: !!checked },
              }))}
            />
            Show timeline from all episodes (archived included)
          </label>
        </div>
      </div>
    );
  };

  const renderStep4_Affiliations = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Program Affiliations</h2>
      <p className="text-sm text-gray-500">
        Link health plans, MCPs, or partners to each program. This step is optional — affiliations can be added later from program settings.
      </p>

      {programs.map((program, pIndex) => (
        <Card key={pIndex} className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline">{program.program_type || 'Program'}</Badge>
            <span className="font-medium">{program.name || `Program ${pIndex + 1}`}</span>
          </div>

          {/* Existing affiliations */}
          {(affiliations[pIndex] || []).map((aff, aIndex) => (
            <div key={aIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-sm">{aff.payer_name || `Plan ${aff.payer_id || aff.health_plan_id}`}</div>
                <div className="text-xs text-gray-500">
                  {aff.affiliation_type} {aff.contract_number && `| Contract: ${aff.contract_number}`}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeAffiliation(pIndex, aIndex)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}

          {/* Add new affiliation */}
          <div className="border-t pt-4 space-y-3">
            <Label className="text-sm font-medium">Add Health Plan / MCP / Payer</Label>

            {/* Search with instant filter */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Search across MCPs, Payers, and Health Plans</Label>
              <div className="flex gap-1">
                <Input
                  value={healthPlanSearch}
                  onChange={(e) => {
                    setHealthPlanSearch(e.target.value);
                    searchAffiliationEntities(e.target.value);
                  }}
                  placeholder="Type to search (e.g. Health Net, SFHP, Molina)..."
                  className="text-sm"
                />
                {healthPlanSearch && (
                  <Button size="sm" variant="ghost" onClick={() => { setHealthPlanSearch(''); setAffiliationSearchResults([]); setShowManualEntry(false); }}>
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {/* Search results dropdown */}
              {affiliationSearchResults.length > 0 && (
                <div className="border rounded-lg max-h-48 overflow-y-auto bg-white shadow-sm">
                  {affiliationSearchResults.map((item: any, idx: number) => (
                    <button
                      key={`${item._source}-${item.id}-${idx}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-b-0 flex items-center justify-between"
                      onClick={() => {
                        const isPayer = item._source === 'payer';
                        setAffiliationForms(prev => ({
                          ...prev,
                          [pIndex]: {
                            ...prev[pIndex],
                            payer_id: isPayer ? item.id : undefined,
                            health_plan_id: isPayer ? undefined : item.id,
                            payer_name: item._display,
                            affiliation_type: isPayer ? 'MCP' as const : 'COMMERCIAL' as const,
                          },
                        }));
                        setAffiliationSearchResults([]);
                        setHealthPlanSearch('');
                        setShowManualEntry(false);
                      }}
                    >
                      <div>
                        <span className="font-medium">{item._display}</span>
                        {item.edi_payer_id && <span className="text-gray-400 ml-1 text-xs">({item.edi_payer_id})</span>}
                        {item.electronic_payer_id && <span className="text-gray-400 ml-1 text-xs">({item.electronic_payer_id})</span>}
                      </div>
                      <Badge variant="outline" className="text-xs ml-2">
                        {item._source === 'payer' ? 'MCP/Payer' : 'Health Plan'}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}

              {/* No results + manual entry option */}
              {showManualEntry && healthPlanSearch.length > 0 && (
                <div className="border rounded-lg p-3 bg-amber-50 space-y-2">
                  <p className="text-sm text-amber-700">No matching payers or health plans found for "{healthPlanSearch}".</p>
                  <p className="text-xs text-gray-500">Enter the details manually below:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Entity Name</Label>
                      <Input
                        value={affiliationForms[pIndex]?.payer_name || healthPlanSearch}
                        onChange={(e) => setAffiliationForms(prev => ({
                          ...prev,
                          [pIndex]: { ...prev[pIndex], payer_name: e.target.value },
                        }))}
                        placeholder="Organization name"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={affiliationForms[pIndex]?.affiliation_type || 'MCP'}
                        onValueChange={(val) => setAffiliationForms(prev => ({
                          ...prev,
                          [pIndex]: { ...prev[pIndex], affiliation_type: val as any },
                        }))}
                      >
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {AFFILIATION_TYPES.map(at => (
                            <SelectItem key={at.value} value={at.value}>{at.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => {
                    setAffiliationForms(prev => ({
                      ...prev,
                      [pIndex]: {
                        ...prev[pIndex],
                        payer_name: prev[pIndex]?.payer_name || healthPlanSearch,
                        payer_id: -1, // Flag for manual entry — will need to create on activation
                        affiliation_type: prev[pIndex]?.affiliation_type || 'MCP',
                      },
                    }));
                    setShowManualEntry(false);
                    setHealthPlanSearch('');
                    setAffiliationSearchResults([]);
                  }}>
                    Use Manual Entry
                  </Button>
                </div>
              )}

              {/* Show all available payers if no search term */}
              {!healthPlanSearch && availablePayers.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">Available MCPs / Payers ({availablePayers.length})</Label>
                  <div className="border rounded-lg max-h-48 overflow-y-auto bg-white">
                    {availablePayers.map((p) => (
                      <button
                        key={`payer-${p.id}`}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-b-0 flex items-center justify-between"
                        onClick={() => {
                          setAffiliationForms(prev => ({
                            ...prev,
                            [pIndex]: {
                              ...prev[pIndex],
                              payer_id: p.id,
                              health_plan_id: undefined,
                              payer_name: p.payer_name,
                              affiliation_type: 'MCP' as const,
                            },
                          }));
                        }}
                      >
                        <span className="font-medium">{p.payer_name}</span>
                        <span className="text-xs text-gray-400">{p.payer_type}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Selected entity + configuration row */}
            {affiliationForms[pIndex]?.payer_name && (
              <div className="bg-blue-50 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-800">{affiliationForms[pIndex]?.payer_name}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => setAffiliationForms(prev => ({ ...prev, [pIndex]: {} as AffiliationFormData }))}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Affiliation Type</Label>
                    <Select
                      value={affiliationForms[pIndex]?.affiliation_type || 'MCP'}
                      onValueChange={(val) => setAffiliationForms(prev => ({
                        ...prev,
                        [pIndex]: { ...prev[pIndex], affiliation_type: val as any },
                      }))}
                    >
                      <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {AFFILIATION_TYPES.map(at => (
                          <SelectItem key={at.value} value={at.value}>{at.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Contract #</Label>
                    <Input
                      value={affiliationForms[pIndex]?.contract_number || ''}
                      onChange={(e) => setAffiliationForms(prev => ({
                        ...prev,
                        [pIndex]: { ...prev[pIndex], contract_number: e.target.value },
                      }))}
                      placeholder="Optional"
                      className="text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button size="sm" onClick={() => addAffiliation(pIndex)}>
                      <Plus className="h-3 w-3 mr-1" /> Add Affiliation
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );

  const renderStep5_Configuration = () => {
    // Flatten all affiliations for selection
    const allAffiliations = Object.entries(affiliations).flatMap(([pIndex, affs]) =>
      affs.map(aff => ({
        ...aff,
        programIndex: parseInt(pIndex),
        programName: programs[parseInt(pIndex)]?.name || `Program ${parseInt(pIndex) + 1}`,
      }))
    );

    if (allAffiliations.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>No affiliations added yet. You can skip this step or go back to add affiliations.</p>
        </div>
      );
    }

    const activeAff = allAffiliations.find(a => a.id === activeConfigAffiliation) || allAffiliations[0];
    if (!activeConfigAffiliation && allAffiliations.length > 0) {
      setActiveConfigAffiliation(allAffiliations[0].id);
    }

    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Per-Affiliation Configuration</h2>

        {/* Affiliation selector */}
        <div className="flex flex-wrap gap-2">
          {allAffiliations.map(aff => (
            <button
              key={aff.id}
              className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                activeConfigAffiliation === aff.id
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setActiveConfigAffiliation(aff.id)}
            >
              <div className="font-medium">{aff.payer_name || `Plan ${aff.payer_id}`}</div>
              <div className="text-xs text-gray-500">{aff.programName}</div>
            </button>
          ))}
        </div>

        {/* Config tabs */}
        {activeAff && (
          <Tabs value={activeConfigTab} onValueChange={setActiveConfigTab}>
            <TabsList>
              <TabsTrigger value="field-mapping">
                <FileText className="h-3 w-3 mr-1" /> Field Mapping
              </TabsTrigger>
              <TabsTrigger value="import-rules">
                <Shield className="h-3 w-3 mr-1" /> Import Rules
              </TabsTrigger>
              <TabsTrigger value="billing">
                <DollarSign className="h-3 w-3 mr-1" /> Billing
              </TabsTrigger>
              <TabsTrigger value="reporting">
                <BarChart3 className="h-3 w-3 mr-1" /> Reporting
              </TabsTrigger>
            </TabsList>

            <TabsContent value="field-mapping">
              {renderFieldMappingTab(activeAff.id)}
            </TabsContent>
            <TabsContent value="import-rules">
              {renderImportRulesTab(activeAff.id)}
            </TabsContent>
            <TabsContent value="billing">
              {renderBillingTab(activeAff.id)}
            </TabsContent>
            <TabsContent value="reporting">
              {renderReportingTab(activeAff.id)}
            </TabsContent>
          </Tabs>
        )}
      </div>
    );
  };

  // Core member DB columns available for field mapping
  const CORE_DB_FIELDS = [
    { db_column: 'first_name', display_name: 'First Name', group: 'demographics', required: true },
    { db_column: 'last_name', display_name: 'Last Name', group: 'demographics', required: true },
    { db_column: 'date_of_birth', display_name: 'Date of Birth', group: 'demographics', required: true },
    { db_column: 'email', display_name: 'Email', group: 'contact', required: false },
    { db_column: 'phone_number', display_name: 'Phone Number', group: 'contact', required: false },
    { db_column: 'street_address', display_name: 'Street Address', group: 'address', required: false },
    { db_column: 'city', display_name: 'City', group: 'address', required: false },
    { db_column: 'state', display_name: 'State', group: 'address', required: false },
    { db_column: 'zip_code', display_name: 'ZIP Code', group: 'address', required: false },
    { db_column: 'medicaid_id', display_name: 'Medicaid ID (CIN)', group: 'identifiers', required: false },
    { db_column: 'medicare_id', display_name: 'Medicare ID (MBI)', group: 'identifiers', required: false },
    { db_column: 'commercial_insurance_id', display_name: 'Commercial Insurance ID', group: 'identifiers', required: false },
    { db_column: 'member_number', display_name: 'Member Number', group: 'identifiers', required: false },
    { db_column: 'preferred_language', display_name: 'Preferred Language', group: 'demographics', required: false },
    { db_column: 'country', display_name: 'Country', group: 'address', required: false },
    { db_column: 'custom_fields', display_name: 'Custom Fields (JSON)', group: 'custom', required: false },
  ];

  const GROUP_COLORS: Record<string, string> = {
    demographics: 'bg-purple-100 text-purple-700',
    contact: 'bg-green-100 text-green-700',
    address: 'bg-blue-100 text-blue-700',
    identifiers: 'bg-orange-100 text-orange-700',
    custom: 'bg-gray-100 text-gray-700',
  };

  const renderFieldMappingTab = (affiliationId: number) => {
    const mappings = fieldMappings[affiliationId] || [];

    const loadCoreFieldMappings = () => {
      // Use semantic groups if loaded, otherwise use CORE_DB_FIELDS directly
      const coreFields = semanticGroups.length > 0
        ? semanticGroups.map((sg, i) => ({
            affiliation_id: affiliationId,
            db_column: sg.db_column,
            source_field_name: '', // User fills in the MCP's column name
            friendly_display_name: sg.display_name,
            semantic_group: sg.group_key,
            is_required: ['first_name', 'last_name', 'date_of_birth'].includes(sg.db_column),
            is_mcp_recommended: true,
            mcp_display_only: false,
            is_active: true,
            sort_order: i,
          }))
        : CORE_DB_FIELDS.map((f, i) => ({
            affiliation_id: affiliationId,
            db_column: f.db_column,
            source_field_name: '', // User fills in the MCP's column name
            friendly_display_name: f.display_name,
            semantic_group: f.group,
            is_required: f.required,
            is_mcp_recommended: true,
            mcp_display_only: false,
            is_active: true,
            sort_order: i,
          }));
      setFieldMappings(prev => ({ ...prev, [affiliationId]: coreFields }));
    };

    return (
      <div className="space-y-4 mt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">
              Map MCP/payer source fields to your database columns.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              DB Column = your system field. Source Field = what the MCP calls it in their data files.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={loadCoreFieldMappings}
            >
              <Wand2 className="h-3 w-3 mr-1" /> Load Core Fields
            </Button>
          </div>
        </div>

        {/* Mappings table */}
        {mappings.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2">DB Column (Target)</th>
                  <th className="text-left px-3 py-2">Display Name</th>
                  <th className="text-left px-3 py-2">Source Field (MCP)</th>
                  <th className="text-left px-3 py-2">Group</th>
                  <th className="text-center px-3 py-2">Req</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50/50">
                    <td className="px-3 py-2">
                      <Select
                        value={m.db_column}
                        onValueChange={(val) => {
                          const updated = [...mappings];
                          const matchedCore = CORE_DB_FIELDS.find(f => f.db_column === val);
                          updated[i] = {
                            ...m,
                            db_column: val,
                            friendly_display_name: m.friendly_display_name || matchedCore?.display_name || val,
                            semantic_group: matchedCore?.group || m.semantic_group,
                          };
                          setFieldMappings(prev => ({ ...prev, [affiliationId]: updated }));
                        }}
                      >
                        <SelectTrigger className="text-xs h-8 font-mono">
                          <SelectValue placeholder="Select column..." />
                        </SelectTrigger>
                        <SelectContent>
                          {CORE_DB_FIELDS.map(f => (
                            <SelectItem key={f.db_column} value={f.db_column}>
                              <span className="font-mono text-xs">{f.db_column}</span>
                              <span className="text-gray-400 ml-1 text-xs">({f.display_name})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{m.friendly_display_name || '—'}</td>
                    <td className="px-3 py-2">
                      <Input
                        value={m.source_field_name}
                        onChange={(e) => {
                          const updated = [...mappings];
                          updated[i] = { ...m, source_field_name: e.target.value };
                          setFieldMappings(prev => ({ ...prev, [affiliationId]: updated }));
                        }}
                        placeholder="MCP field name..."
                        className="text-xs h-8"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-xs ${GROUP_COLORS[m.semantic_group || ''] || ''}`}>
                        {m.semantic_group || '—'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Checkbox
                        checked={m.is_required}
                        onCheckedChange={(checked) => {
                          const updated = [...mappings];
                          updated[i] = { ...m, is_required: !!checked };
                          setFieldMappings(prev => ({ ...prev, [affiliationId]: updated }));
                        }}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFieldMappings(prev => ({
                            ...prev,
                            [affiliationId]: mappings.filter((_, j) => j !== i),
                          }));
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-400 text-sm border rounded-lg">
            <Upload className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>No field mappings configured.</p>
            <p className="mt-1">Click <strong>"Load Core Fields"</strong> to populate with standard member fields.</p>
          </div>
        )}

        {/* Add mapping row */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setFieldMappings(prev => ({
              ...prev,
              [affiliationId]: [...mappings, {
                affiliation_id: affiliationId,
                db_column: '',
                source_field_name: '',
                friendly_display_name: '',
                semantic_group: '',
                is_required: false,
                is_mcp_recommended: false,
                mcp_display_only: false,
                is_active: true,
              }],
            }));
          }}
        >
          <Plus className="h-3 w-3 mr-1" /> Add Mapping Row
        </Button>
      </div>
    );
  };

  const renderImportRulesTab = (affiliationId: number) => {
    const rules = importRules[affiliationId] || {
      duplicate_detection_fields: [],
      duplicate_match_threshold: 0.95,
      duplicate_primary_field: '',
      duplicate_action: 'FLAG',
      protected_fields: [],
      update_strategy: 'UPDATE_EMPTY_ONLY',
      error_notification_emails: [],
      max_error_rate_percent: 10,
    };

    const updateRules = (field: string, value: any) => {
      setImportRules(prev => ({
        ...prev,
        [affiliationId]: { ...rules, [field]: value },
      }));
    };

    return (
      <div className="space-y-4 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Primary Unique Identifier</Label>
            <Input
              value={rules.duplicate_primary_field || ''}
              onChange={(e) => updateRules('duplicate_primary_field', e.target.value)}
              placeholder="e.g., health_plan_member_id"
              className="text-sm"
            />
          </div>
          <div>
            <Label className="text-sm">Duplicate Detection Fields (comma-separated)</Label>
            <Input
              value={(rules.duplicate_detection_fields || []).join(', ')}
              onChange={(e) => updateRules('duplicate_detection_fields', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="e.g., first_name, last_name, date_of_birth"
              className="text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">On Duplicate Found</Label>
            <Select
              value={rules.duplicate_action || 'FLAG'}
              onValueChange={(val) => updateRules('duplicate_action', val)}
            >
              <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FLAG">Flag for review</SelectItem>
                <SelectItem value="SKIP">Skip record</SelectItem>
                <SelectItem value="MERGE">Auto-merge</SelectItem>
                <SelectItem value="UPDATE">Update existing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Update Strategy</Label>
            <Select
              value={rules.update_strategy || 'UPDATE_EMPTY_ONLY'}
              onValueChange={(val) => updateRules('update_strategy', val)}
            >
              <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UPDATE_ALL">Update all fields</SelectItem>
                <SelectItem value="UPDATE_EMPTY_ONLY">Only fill empty fields</SelectItem>
                <SelectItem value="MANUAL_REVIEW">Manual review required</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-sm">Protected Fields (never overwritten on import, comma-separated)</Label>
          <Input
            value={(rules.protected_fields || []).join(', ')}
            onChange={(e) => updateRules('protected_fields', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            placeholder="e.g., assigned_to, case_notes"
            className="text-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Error Notification Emails (comma-separated)</Label>
            <Input
              value={(rules.error_notification_emails || []).join(', ')}
              onChange={(e) => updateRules('error_notification_emails', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="admin@org.com, ops@org.com"
              className="text-sm"
            />
          </div>
          <div>
            <Label className="text-sm">Max Error Rate (%)</Label>
            <Input
              type="number"
              value={rules.max_error_rate_percent || 10}
              onChange={(e) => updateRules('max_error_rate_percent', parseFloat(e.target.value))}
              className="text-sm max-w-32"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderBillingTab = (affiliationId: number) => {
    const config = billingConfigs[affiliationId] || {
      billing_model: 'FEE_FOR_SERVICE',
      service_codes: [],
      claim_format: '837P',
    };

    return (
      <div className="space-y-4 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Billing Model</Label>
            <Select
              value={config.billing_model || 'FEE_FOR_SERVICE'}
              onValueChange={(val) => setBillingConfigs(prev => ({
                ...prev,
                [affiliationId]: { ...config, billing_model: val as any },
              }))}
            >
              <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PMPM">Per Member Per Month (PMPM)</SelectItem>
                <SelectItem value="FEE_FOR_SERVICE">Fee for Service</SelectItem>
                <SelectItem value="CAPITATED">Capitated</SelectItem>
                <SelectItem value="HYBRID">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Claim Format</Label>
            <Select
              value={config.claim_format || '837P'}
              onValueChange={(val) => setBillingConfigs(prev => ({
                ...prev,
                [affiliationId]: { ...config, claim_format: val as any },
              }))}
            >
              <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="837P">837P (Professional)</SelectItem>
                <SelectItem value="837I">837I (Institutional)</SelectItem>
                <SelectItem value="CUSTOM_CSV">Custom CSV</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-sm">Payer-Specific Provider ID</Label>
          <Input
            value={config.payer_specific_id || ''}
            onChange={(e) => setBillingConfigs(prev => ({
              ...prev,
              [affiliationId]: { ...config, payer_specific_id: e.target.value },
            }))}
            placeholder="ID assigned by this payer"
            className="text-sm max-w-sm"
          />
        </div>
      </div>
    );
  };

  const renderReportingTab = (affiliationId: number) => {
    const configs = reportConfigs[affiliationId] || [];

    return (
      <div className="space-y-4 mt-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">Configure report templates for this MCP.</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setReportConfigs(prev => ({
                ...prev,
                [affiliationId]: [...configs, {
                  affiliation_id: affiliationId,
                  report_name: '',
                  report_type: 'ENROLLMENT',
                  included_fields: [],
                  field_order: [],
                  field_display_names: {},
                  output_format: 'CSV',
                  frequency: 'MONTHLY',
                  delivery_method: 'PORTAL',
                  is_active: true,
                  auto_generate: false,
                }],
              }));
            }}
          >
            <Plus className="h-3 w-3 mr-1" /> Add Report
          </Button>
        </div>

        {configs.map((rc, i) => (
          <Card key={i} className="p-4 space-y-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Report {i + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setReportConfigs(prev => ({
                    ...prev,
                    [affiliationId]: configs.filter((_, j) => j !== i),
                  }));
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Report Name</Label>
                <Input
                  value={rc.report_name}
                  onChange={(e) => {
                    const updated = [...configs];
                    updated[i] = { ...rc, report_name: e.target.value };
                    setReportConfigs(prev => ({ ...prev, [affiliationId]: updated }));
                  }}
                  placeholder="Monthly enrollment"
                  className="text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select
                  value={rc.report_type}
                  onValueChange={(val) => {
                    const updated = [...configs];
                    updated[i] = { ...rc, report_type: val as any };
                    setReportConfigs(prev => ({ ...prev, [affiliationId]: updated }));
                  }}
                >
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ENROLLMENT">Enrollment</SelectItem>
                    <SelectItem value="UTILIZATION">Utilization</SelectItem>
                    <SelectItem value="OUTCOMES">Outcomes</SelectItem>
                    <SelectItem value="BILLING">Billing</SelectItem>
                    <SelectItem value="CUSTOM">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Frequency</Label>
                <Select
                  value={rc.frequency}
                  onValueChange={(val) => {
                    const updated = [...configs];
                    updated[i] = { ...rc, frequency: val as any };
                    setReportConfigs(prev => ({ ...prev, [affiliationId]: updated }));
                  }}
                >
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DAILY">Daily</SelectItem>
                    <SelectItem value="WEEKLY">Weekly</SelectItem>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                    <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                    <SelectItem value="ON_DEMAND">On Demand</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Output Format</Label>
                <Select
                  value={rc.output_format}
                  onValueChange={(val) => {
                    const updated = [...configs];
                    updated[i] = { ...rc, output_format: val as any };
                    setReportConfigs(prev => ({ ...prev, [affiliationId]: updated }));
                  }}
                >
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CSV">CSV</SelectItem>
                    <SelectItem value="XLSX">Excel (XLSX)</SelectItem>
                    <SelectItem value="PDF">PDF</SelectItem>
                    <SelectItem value="EDI">EDI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Delivery</Label>
                <Select
                  value={rc.delivery_method}
                  onValueChange={(val) => {
                    const updated = [...configs];
                    updated[i] = { ...rc, delivery_method: val as any };
                    setReportConfigs(prev => ({ ...prev, [affiliationId]: updated }));
                  }}
                >
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SFTP">SFTP</SelectItem>
                    <SelectItem value="EMAIL">Email</SelectItem>
                    <SelectItem value="PORTAL">Portal</SelectItem>
                    <SelectItem value="API">API</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        ))}

        {configs.length === 0 && (
          <p className="text-center text-gray-400 text-sm py-4">No report templates configured.</p>
        )}
      </div>
    );
  };

  // Load staff when reaching step 6
  useEffect(() => {
    if (currentStep === 6 && selectedOrgId) {
      loadOrgStaff(selectedOrgId);
    }
  }, [currentStep, selectedOrgId]);

  const renderStep6_Staff = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Staff Assignments</h2>

        {/* Existing assignments */}
        {staffAssignments.length > 0 && (
          <div className="space-y-2">
            {staffAssignments.map((sa, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-sm">{sa.user_name || `User ${sa.user_id}`}</div>
                  <div className="text-xs text-gray-500">
                    {sa.program_name} | {sa.role_in_program}
                    {sa.max_caseload ? ` | Max: ${sa.max_caseload}` : ''}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeStaffAssignment(i)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add assignment form */}
        <Card className="p-4 space-y-3">
          <Label className="text-sm font-medium">Add Staff Assignment</Label>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Staff Member</Label>
              <Select
                value={staffForm.user_id?.toString() || ''}
                onValueChange={(val) => setStaffForm(prev => ({ ...prev, user_id: parseInt(val) }))}
              >
                <SelectTrigger className="text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {orgStaff.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>
                      {s.name || s.email || `User ${s.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Program</Label>
              <Select
                value={staffForm.program_id?.toString() || ''}
                onValueChange={(val) => setStaffForm(prev => ({ ...prev, program_id: parseInt(val) }))}
              >
                <SelectTrigger className="text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {programs.map((p, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {p.name || `Program ${i + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select
                value={staffForm.role_in_program || 'coordinator'}
                onValueChange={(val) => setStaffForm(prev => ({ ...prev, role_in_program: val as any }))}
              >
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROGRAM_ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Max Caseload</Label>
              <Input
                type="number"
                value={staffForm.max_caseload || ''}
                onChange={(e) => setStaffForm(prev => ({ ...prev, max_caseload: parseInt(e.target.value) || undefined }))}
                placeholder="Optional"
                className="text-sm"
              />
            </div>
          </div>
          <Button size="sm" onClick={addStaffAssignment} disabled={!staffForm.user_id || staffForm.program_id === undefined}>
            <Plus className="h-3 w-3 mr-1" /> Assign
          </Button>
        </Card>

        {staffAssignments.length === 0 && orgStaff.length === 0 && (
          <div className="text-center py-6 text-gray-400 text-sm">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>No staff found for this organization. Staff can be assigned after activation.</p>
          </div>
        )}
      </div>
    );
  };

  // ── Step 7: Form Templates ────────────────────────────────────────────

  const loadSourceTemplates = async (sourceCboId: string) => {
    setCloneSourceCboId(sourceCboId);
    setSelectedTemplateIds(new Set());
    if (!sourceCboId) {
      setAvailableFormTemplates([]);
      setCloneSourceOrgName('');
      return;
    }
    setFormTemplatesLoading(true);
    try {
      const result = await formsAPI.listTemplatesAvailableForClone(parseInt(sourceCboId, 10));
      setAvailableFormTemplates(result.templates);
      setCloneSourceOrgName(result.source_organization.name);
    } catch {
      setAvailableFormTemplates([]);
    } finally {
      setFormTemplatesLoading(false);
    }
  };

  const toggleTemplateSelection = (id: number) => {
    setSelectedTemplateIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllTemplates = () => {
    if (selectedTemplateIds.size === availableFormTemplates.length) {
      setSelectedTemplateIds(new Set());
    } else {
      setSelectedTemplateIds(new Set(availableFormTemplates.map((t: any) => t.id)));
    }
  };

  const handleBulkClone = async () => {
    if (!cloneSourceCboId || selectedTemplateIds.size === 0 || !selectedOrgId) return;
    setFormTemplatesLoading(true);
    try {
      const result = await formsAPI.bulkCloneFormTemplates(
        parseInt(cloneSourceCboId, 10),
        Array.from(selectedTemplateIds),
        selectedOrgId
      );
      setClonedTemplates(result.cloned);
      if (result.error_count > 0) {
        alert(`Cloned ${result.cloned_count} templates. ${result.error_count} failed.`);
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to clone templates');
    } finally {
      setFormTemplatesLoading(false);
    }
  };

  const renderStep7_FormTemplates = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Form Templates</h2>
        <p className="text-sm text-gray-600">
          Clone form templates from an existing organization to give this org pre-built intake forms, assessments, and care plans.
          This step is optional — you can always clone or create templates later.
        </p>

        {/* Already cloned */}
        {clonedTemplates.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-800">
                {clonedTemplates.length} template{clonedTemplates.length !== 1 ? 's' : ''} cloned successfully
              </span>
            </div>
            <ul className="text-sm text-green-700 list-disc list-inside">
              {clonedTemplates.map((ct: any) => (
                <li key={ct.new_template_id}>{ct.name}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Source org picker */}
        <Card className="p-4 space-y-4">
          <Label className="text-sm font-medium">Source Organization</Label>
          <Select value={cloneSourceCboId} onValueChange={loadSourceTemplates}>
            <SelectTrigger>
              <SelectValue placeholder="Select an organization to clone templates from..." />
            </SelectTrigger>
            <SelectContent>
              {existingOrgs
                .filter((o: Organization) => o.id !== selectedOrgId)
                .map((o: Organization) => (
                  <SelectItem key={o.id} value={String(o.id)}>
                    {o.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {formTemplatesLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading templates...
            </div>
          )}

          {cloneSourceCboId && !formTemplatesLoading && availableFormTemplates.length === 0 && (
            <p className="text-sm text-gray-500">No active form templates found in this organization.</p>
          )}

          {availableFormTemplates.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {availableFormTemplates.length} template{availableFormTemplates.length !== 1 ? 's' : ''} available from {cloneSourceOrgName}
                </span>
                <Button variant="outline" size="sm" onClick={toggleAllTemplates}>
                  {selectedTemplateIds.size === availableFormTemplates.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                {availableFormTemplates.map((tmpl: any) => (
                  <label
                    key={tmpl.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedTemplateIds.has(tmpl.id)}
                      onCheckedChange={() => toggleTemplateSelection(tmpl.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{tmpl.name}</div>
                      <div className="text-xs text-gray-500">
                        {tmpl.form_type?.replace('_', ' ')}
                        {tmpl.program_name ? ` · ${tmpl.program_name}` : ''}
                        {` · v${tmpl.version}`}
                      </div>
                    </div>
                    {tmpl.description && (
                      <span className="text-xs text-gray-400 truncate max-w-[200px] hidden md:block">
                        {tmpl.description}
                      </span>
                    )}
                  </label>
                ))}
              </div>

              <Button
                variant="primary"
                icon={<Copy className="w-4 h-4" />}
                disabled={selectedTemplateIds.size === 0 || formTemplatesLoading}
                onClick={handleBulkClone}
              >
                {formTemplatesLoading
                  ? 'Cloning...'
                  : `Clone ${selectedTemplateIds.size} Template${selectedTemplateIds.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  };

  const renderStep8_Review = () => {
    const totalAffiliations = Object.values(affiliations).reduce((sum, affs) => sum + affs.length, 0);
    const totalMappings = Object.values(fieldMappings).reduce((sum, m) => sum + m.length, 0);

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Review & Activate</h2>

        {/* Organization Summary */}
        <Card className="p-4">
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Organization
          </h3>
          <div className="text-sm text-gray-600">
            <p><strong>Name:</strong> {orgData.name || `Organization #${selectedOrgId}`}</p>
            {orgData.contact_email && <p><strong>Email:</strong> {orgData.contact_email}</p>}
            {orgData.address && <p><strong>Address:</strong> {orgData.address}</p>}
          </div>
        </Card>

        {/* Programs Summary */}
        <Card className="p-4">
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <Target className="h-4 w-4" /> Programs ({programs.length})
          </h3>
          <div className="space-y-2">
            {programs.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="text-xs">{p.record_model}</Badge>
                <span>{p.name}</span>
                <span className="text-gray-400">({p.program_type})</span>
                {(affiliations[i] || []).length > 0 && (
                  <Badge className="text-xs bg-blue-100 text-blue-700">
                    {(affiliations[i] || []).length} affiliation(s)
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Config Summary */}
        <Card className="p-4">
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <Settings className="h-4 w-4" /> Configuration
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Affiliations</div>
              <div className="text-lg font-semibold">{totalAffiliations}</div>
            </div>
            <div>
              <div className="text-gray-500">Field Mappings</div>
              <div className="text-lg font-semibold">{totalMappings}</div>
            </div>
            <div>
              <div className="text-gray-500">Staff Assigned</div>
              <div className="text-lg font-semibold">{staffAssignments.length}</div>
            </div>
            <div>
              <div className="text-gray-500">Report Templates</div>
              <div className="text-lg font-semibold">
                {Object.values(reportConfigs).reduce((sum, r) => sum + r.length, 0)}
              </div>
            </div>
          </div>
        </Card>

        {/* Episode/Household configs */}
        {Object.keys(episodeConfigs).length > 0 && (
          <Card className="p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <FileStack className="h-4 w-4" /> Episode Rules
            </h3>
            {Object.entries(episodeConfigs).map(([idx, config]) => (
              <div key={idx} className="text-sm">
                <span className="font-medium">{programs[parseInt(idx)]?.name}:</span>{' '}
                {config.rules.length} detection rule(s)
              </div>
            ))}
          </Card>
        )}

        {Object.keys(householdConfigs).length > 0 && (
          <Card className="p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <FileStack className="h-4 w-4" /> Household Config
            </h3>
            {Object.entries(householdConfigs).map(([idx, config]) => (
              <div key={idx} className="text-sm">
                <span className="font-medium">{programs[parseInt(idx)]?.name}:</span>{' '}
                {config.serviceCategories.length} service categories
              </div>
            ))}
          </Card>
        )}

        {/* Activate Button */}
        <div className="flex justify-center pt-4">
          <Button
            size="lg"
            onClick={handleActivate}
            disabled={loading}
            className="px-8"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Activating...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" /> Activate Organization
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // ── Step Content Router ────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderStep1_Organization();
      case 2: return renderStep2_Programs();
      case 3: return renderStep3_RecordModel();
      case 4: return renderStep4_Affiliations();
      case 5: return renderStep5_Configuration();
      case 6: return renderStep6_Staff();
      case 7: return renderStep7_FormTemplates();
      case 8: return renderStep8_Review();
      default: return null;
    }
  };

  const currentStepConfig = WIZARD_STEPS.find(s => s.id === currentStep);

  // ── Main Render ────────────────────────────────────────────────────────

  // ── Draft Resume Dialog ────────────────────────────────────────────────

  if (showDraftResume && existingDrafts.length > 0) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <Wand2 className="h-7 w-7 text-blue-600" />
          <h1 className="text-2xl font-bold">Resume Setup?</h1>
        </div>
        <Card className="p-6 mb-4">
          <p className="text-sm text-gray-600 mb-4">
            You have unfinished organization setup drafts. Would you like to continue where you left off?
          </p>
          <div className="space-y-3">
            {existingDrafts.map((d: any) => (
              <div
                key={d.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-blue-50 cursor-pointer"
                onClick={() => resumeExistingDraft(d)}
              >
                <div>
                  <div className="font-medium text-sm">{d.cbo_name || `Organization #${d.cbo_id}`}</div>
                  <div className="text-xs text-gray-500">
                    Step {d.current_step} of {WIZARD_STEPS.length} &middot; Last updated {d.updated_at ? fmt.dateTime(d.updated_at) : 'recently'}
                  </div>
                </div>
                <Badge variant="outline">{d.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
        <div className="flex justify-between">
          <Button variant="outline" onClick={discardAndCreateNew}>
            Start Fresh
          </Button>
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          {!tabbed && <Wand2 className="h-7 w-7 text-blue-600" />}
          {!tabbed && <h1 className="text-3xl font-bold">Organization Setup Wizard</h1>}
          {/* Auto-save status */}
          <div className="ml-auto text-xs text-gray-400 flex items-center gap-2">
            {savingDraft ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</>
            ) : lastSavedAt ? (
              <><CheckCircle2 className="h-3 w-3 text-green-500" /> Saved at {lastSavedAt}</>
            ) : draft?.id ? (
              <span>Draft #{draft.id}</span>
            ) : null}
          </div>
        </div>
        <p className="text-gray-600">
          Configure a new organization with programs, health plan affiliations, and staff assignments.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {WIZARD_STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <StepIcon className="h-5 w-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${isActive ? 'font-semibold' : ''}`}>
                    {step.name}
                  </span>
                </div>
                {index < WIZARD_STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      step.id < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <Progress value={(currentStep / WIZARD_STEPS.length) * 100} className="h-2" />
      </div>

      {/* Guidance */}
      {currentStepConfig && (
        <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
          <p className="text-sm text-blue-800">{currentStepConfig.guidance}</p>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="p-4 mb-6 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        </Card>
      )}

      {/* Step Content */}
      <Card className="p-6 mb-6">
        {loading && currentStep === 1 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          renderStepContent()
        )}
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <div>
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          {currentStep < WIZARD_STEPS.length && (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationSetupWizard;
